import { Box, type BoxProps } from '@mui/material';
import { brand } from '../../theme';

export type LogoTone = 'onLight' | 'onDark';

export interface LogoProps extends Omit<BoxProps, 'children'> {
  /** Pixel height of the mark; the wordmark scales relative to it. */
  height?: number;
  /** Foreground tone. `onLight` = ink text (for cream surfaces); `onDark` = paper text (for ink surfaces, e.g. the AppBar). */
  tone?: LogoTone;
  /** Render only the connector mark, without the "TradeConnector" wordmark. */
  markOnly?: boolean;
}

/**
 * Trade Connector brand logo — a connector mark (hollow node + line + filled
 * emerald node) followed by the "Trade Connector" wordmark, with "Connector"
 * in brand green. Replaces the legacy logo.png raster.
 *
 * Use `tone="onDark"` on ink surfaces (AppBar, footer) and `tone="onLight"`
 * (default) on cream/paper surfaces.
 */
export default function Logo({
  height = 51,
  tone = 'onLight',
  markOnly = false,
  sx,
  ...boxProps
}: LogoProps) {
  const fg = tone === 'onDark' ? brand.paper : brand.ink;
  // Mark intrinsic ratio is 97.1:99.0 from public/icon.svg.
  // Matches the design: a 20px mark pairs with a 19px wordmark and an ~11px gap.
  const markWidth = (height * 97.1) / 99.0;
  // Shrink text and gap back to their original proportion (approx 1/1.44 scale of height)
  const fontSize = height * 0.66;
  const strokeColor = tone === 'onDark' ? '#ffffff' : fg;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${Math.round(height * 0.38)}px`,
        textDecoration: 'none',
        lineHeight: 1,
        ...sx,
      }}
      {...boxProps}
    >
      <Box
        component="svg"
        width={markWidth}
        height={height}
        viewBox="-48.6 -55.0 97.1 99.0"
        fill="none"
        aria-hidden="true"
        sx={{ flexShrink: 0, display: 'block' }}
      >
        <defs>
          <clipPath id="sA" clipPathUnits="userSpaceOnUse">
            <path
              d="M-53.6 -60.0 L53.6 -60.0 L53.6 49.0 L-53.6 49.0 Z M-37.76 -9.40 L-11.78 -24.40 L-7.28 -16.60 L-33.26 -1.60 Z M7.96 -13.00 L7.96 17.00 L-1.04 17.00 L-1.04 -13.00 Z"
              clipRule="evenodd"
            />
          </clipPath>
          <clipPath id="sB" clipPathUnits="userSpaceOnUse">
            <path
              d="M-53.6 -60.0 L53.6 -60.0 L53.6 49.0 L-53.6 49.0 Z M10.74 37.40 L-15.24 22.40 L-10.74 14.60 L15.24 29.60 Z M-15.24 -0.40 L10.74 -15.40 L15.24 -7.60 L-10.74 7.40 Z"
              clipRule="evenodd"
            />
          </clipPath>
          <clipPath id="sC" clipPathUnits="userSpaceOnUse">
            <path
              d="M-53.6 -60.0 L53.6 -60.0 L53.6 49.0 L-53.6 49.0 Z M7.28 13.40 L-18.70 -1.60 L-14.20 -9.40 L11.78 5.60 Z M27.02 -28.00 L27.02 2.00 L18.02 2.00 L18.02 -28.00 Z"
              clipRule="evenodd"
            />
          </clipPath>
        </defs>
        <path
          d="M0.00 -48.00 L-22.52 -35.00 L-22.52 -9.00 L-0.00 4.00 L22.52 -9.00 L22.52 -35.00 Z"
          fill="none"
          stroke={strokeColor}
          strokeWidth={5}
          strokeLinejoin="miter"
          strokeMiterlimit={10}
          clipPath="url(#sA)"
        />
        <path
          d="M-19.05 -15.00 L-41.57 -2.00 L-41.57 24.00 L-19.05 37.00 L3.46 24.00 L3.46 -2.00 Z"
          fill="none"
          stroke={strokeColor}
          strokeWidth={5}
          strokeLinejoin="miter"
          strokeMiterlimit={10}
          clipPath="url(#sB)"
        />
        <path
          d="M19.05 -15.00 L-3.46 -2.00 L-3.46 24.00 L19.05 37.00 L41.57 24.00 L41.57 -2.00 Z"
          fill="none"
          stroke={strokeColor}
          strokeWidth={5}
          strokeLinejoin="miter"
          strokeMiterlimit={10}
          clipPath="url(#sC)"
        />
      </Box>
      {!markOnly && (
        <Box
          component="span"
          sx={{
            fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
            fontWeight: 700,
            fontSize: `${fontSize}px`,
            letterSpacing: '-0.01em',
            color: fg,
            whiteSpace: 'nowrap',
          }}
        >
          Trade<Box component="b" sx={{ color: brand.accent, fontWeight: 700 }}>Connector</Box>
        </Box>
      )}
    </Box>
  );
}
